import Vue from 'vue';
import {isNil} from '@/helpers/utils';
import {FlagCheckSections, FlagStates} from '@/helpers/constants';
import {resolveAssignmentType} from '@/helpers/baseConfig';
import {
   getRelevantFlags,
   hasUnresolvedFlags as hasUnresolvedExpenseFlags,
} from '@/helpers/expenseCompletion';
import {
   buildSaveRequestKey,
   buildSetZeroPayload,
   cloneActivitiesData,
   cloneProjectsData,
   findImportedVendorRowIndex,
   findManualVendorRowIndex,
   findSummaryRowInState,
   getManualVendorId,
   getVisibleAssignedUsers,
   locationsMatch,
   resolveCanonicalAssigneeId,
   snapshotSummaryRow,
} from './expense-data-identity';
import {
   buildDummySummary,
   buildManualSummaryPeriodsPayload,
   extractSummaryResults,
   manualVendorPayload,
   mapSummariesToVendorEntries,
   normalizeManualVendor,
} from './expense-data-normalizers';
import {
   setActivitiesDataWithColumns,
   setPercentageWithTimestamp,
   setProjectPercentageValue,
   setProjectsDataWithColumns,
   setSingleActivityValue,
   setSingleProjectValue,
   zeroActivitiesFromAllKnownKeys,
   zeroProjectsFromAllKnownKeys,
} from './expense-data-mutations';
import {collectStatusesByAssignee} from './expense-data-status';
import {filterVendorsBySupportedPeriods} from './expense-data-periods';
import {
   applyNetAmountToRowBySection,
   rollbackNetAmountToRowBySection,
} from './expense-data-section-strategy';

const FLAG_CHECK_SECTION_BY_NAME = Object.freeze({
   supplies: FlagCheckSections.SUPPLIES,
   cloudcomputing: FlagCheckSections.CLOUD_COMPUTING,
   contractors: FlagCheckSections.CONTRACT_RESEARCH,
   employees: FlagCheckSections.EMPLOYEE_TIME,
});

// A Complete-reading assignee must not show Complete while they still have an
// unresolved flag for that period -- the same signal "Mark Section Complete"
// already gates on (see isSectionMarkCompletedDisabled), surfaced on the status
// badge itself (collectStatusesByAssignee downgrades Complete -> In Progress).
// checkSection's own ruleName tag keeps this correct even for a section whose
// flags aren't actively (re)loaded (e.g. contractors): stale flags left over
// from another section never match this section's checkSection.
const buildHasUnresolvedFlagsForAssignee =
   ({getters, rootGetters}) =>
   (assigneeId, periodId) => {
      const period = rootGetters['companies/studyPeriodMap']?.[periodId]?.period;
      const relevantFlags = getRelevantFlags({
         flags: rootGetters['flags/flags'] || [],
         period,
         studyId: rootGetters['companies/activeStudyId'],
         checkSection: getters.flagCheckSection,
         userId: assigneeId,
         includeAllUsers: false,
      });

      return hasUnresolvedExpenseFlags(relevantFlags, FlagStates);
   };

// zeroProjectsFromAllKnownKeys/zeroActivitiesFromAllKnownKeys need every project/activity
// id relevant to a row's period, not just whichever ones the row already happens to have
// a key for (a never-touched project/activity has no key yet, so it would otherwise be
// silently skipped when zeroing). Activities apply only to employees.
const resolveZeroableSplitIds = ({sectionName, rootGetters, periodId}) => {
   const projectIds = (rootGetters['projects/projectsInPeriod']?.(periodId) ?? []).map(
      ({id}) => id
   );
   const activityIds =
      sectionName === 'employees'
         ? (rootGetters['activities/activities'] ?? []).map(({id}) => id)
         : [];

   return {projectIds, activityIds};
};

const filterEmployeePlaceholderRows = (sectionName, rowsByPeriod) => {
   if (sectionName !== 'employees') {
      return rowsByPeriod;
   }

   return Object.fromEntries(
      Object.entries(rowsByPeriod).map(([periodId, rows]) => [
         periodId,
         (rows ?? []).filter((row) => row.id !== null),
      ])
   );
};

/**
 * Optional module parts that can override defaults produced by the factory.
 * @typedef {Object} ExpenseOverrides
 * @property {boolean} [namespaced]
 * @property {Function} [state]
 * @property {Object<string, Function>} [getters]
 * @property {Object<string, Function>} [mutations]
 * @property {Object<string, Function>} [actions]
 */

/**
 * Helper for IDE type inference on module override objects used by expenseDataModuleFactory.
 * @param {ExpenseOverrides} overrides
 * @returns {ExpenseOverrides}
 */
export const defineExpenseOverrides = (overrides) => overrides;

/**
 * Factory for expense Vuex modules sharing summary/vendor assignment flows.
 * @param {'supplies'|'cloudcomputing'|'contractors'|'employees'|string} sectionName
 * @param {string} documentsUploadCategory
 * @param {{
 *   namespaced?: boolean,
 *   state?: Object|Function,
 *   getters?: Object<string, Function>,
 *   mutations?: Object<string, Function>,
 *   actions?: Object<string, Function>
 * }} [overrides={}]
 * @returns {{
 *   namespaced: boolean,
 *   state: Function,
 *   getters: Object<string, Function>,
 *   mutations: Object<string, Function>,
 *   actions: Object<string, Function>
 * }}
 */
let _saveRequestCounter = 0;

export const expenseDataModuleFactory = (
   sectionName,
   documentsUploadCategory,
   {namespaced = true, state = {}, getters = {}, mutations = {}, actions = {}} = {}
) => ({
   namespaced,

   state() {
      return {
         importedVendors: {},
         manualVendors: {},
         accountData: {},
         adHocData: {},
         periodIds: [],
         documentsUploadCategoryId: null,
         latestSaveRequestByKey: {},
         // Set on every successful summary-field save; cleared once flags are
         // reloaded. Lets "Mark Assignment Complete" re-enable immediately after
         // an edit, rather than staying blocked by a flag the server hasn't had
         // a chance to re-validate yet (see isAssignmentMarkCompletedDisabled).
         flagsStale: false,
         ...state,
      };
   },

   getters: {
      isCompleted: (state, getters, rootState, rootGetters) => {
         return rootGetters['companies/activeStudy'][sectionName].completedAt !== null;
      },
      hasPendingSaveRequests: (state) => Object.keys(state.latestSaveRequestByKey).length > 0,
      flagsStale: (state) => state.flagsStale,
      declaration: (state, getters, rootState, rootGetters) => {
         return rootGetters['companies/activeStudy'][sectionName].wasUsed;
      },
      accountData: (state) => state.accountData,
      adHocData: (state) => state.adHocData,
      assignee: (state, getters, rootState, rootGetters) => {
         const assignmentsKey = resolveAssignmentType(sectionName);
         const assignee = rootGetters['companies/activeStudy'][assignmentsKey].find(
            (assignment) => assignment.completed === null
         );
         return assignee ? assignee.assigneeId : null;
      },
      hasUncompletedAssignment: (state, getters, rootState, rootGetters) => {
         const userId = rootGetters.profile.id;
         const assignmentsKey = resolveAssignmentType(sectionName);
         const assignments = rootGetters['companies/activeStudy'][assignmentsKey].filter(
            (assignment) => assignment.completed === null && assignment.assigneeId == userId
         );
         return assignments.length > 0;
      },
      hasSectionAssignment: (state, getters, rootState, rootGetters) => {
         const userId = rootGetters.profile.id;
         const assignmentsKey = resolveAssignmentType(sectionName);
         const assignments = rootGetters['companies/activeStudy'][assignmentsKey].filter(
            (assignment) => assignment.assigneeId == userId
         );
         return assignments.length > 0;
      },
      importedVendors: ({importedVendors}) => Object.values(importedVendors),
      manualVendors: ({manualVendors}) => Object.values(manualVendors),
      vendorsMap: (state) => state.manualVendors,
      importedVendorsMap: (state) => state.importedVendors,
      hasVendors: (state, getters, rootState, rootGetters) => {
         if (rootGetters.isSME) {
            const profileId = rootGetters.profile?.id;
            return [...getters.importedVendors, ...getters.manualVendors].some(
               ({assigneeId}) => assigneeId === profileId
            );
         }

         return getters.importedVendors.length !== 0 || getters.manualVendors.length !== 0;
      },
      flagCheckSection: () => FLAG_CHECK_SECTION_BY_NAME[sectionName],
      // Base period list before the employees/SME assignment restriction below is applied.
      // Used when picking periods for a brand-new employee, who by definition has no
      // existing assignment yet -- restricting to assignedUsersByPeriod there would make
      // it impossible for an SME with no prior assignments to ever add a first employee.
      baseSupportedPeriodIds: (state, getters, rootState, rootGetters) => {
         // TODO: Remove when unconfigured project assignment is supported.
         // Skip periods where no project is assigned.
         const activeStudyPeriods = rootGetters['companies/activeStudyPeriods'] || [];
         const supportedPeriodIds = rootGetters['projects/projectsSupportedPeriodIds'] || [];

         return activeStudyPeriods
            .filter(({id}) => supportedPeriodIds.includes(id))
            .map(({id}) => id);
      },
      supportedPeriodIds: (state, getters, rootState, rootGetters) => {
         return getters.baseSupportedPeriodIds.filter((id) => {
            if (sectionName !== 'employees' || !rootGetters.isSME) {
               return true;
            }

            return getters.assignedUsersByPeriod(id).length > 0;
         });
      },
      // Unlike contractors/supplies/cloud, employees have no backend auto-expansion of
      // summary rows across periods, and the all-assignments endpoint returns one row
      // per (employee, period) pair -- since state.manualVendors/importedVendors are
      // keyed by employee id, committing those rows collapses each employee down to
      // whichever period's row was committed last, so `.period` on the stored entry
      // reflects only one arbitrary period, not every period the employee belongs to.
      // Cross-reference adHocData instead of trusting the stored `.period`.
      employeeIdsWithRealRowForPeriod: (state) => (periodId) =>
         new Set(
            (state.adHocData[periodId] ?? [])
               .filter((row) => row.id !== null)
               .map((row) => getManualVendorId(row))
         ),
      importedVendorsByPeriod: (state, getters, rootState, rootGetters) => (periodId) => {
         if (sectionName === 'employees') {
            const idsWithRealRow = getters.employeeIdsWithRealRowForPeriod(periodId);
            return getters.importedVendors.filter(({id}) => idsWithRealRow.has(id));
         }

         const period = rootGetters['companies/studyPeriodMap']?.[periodId]?.period;
         if (!period) return [];

         return getters.importedVendors.filter(
            ({period: vendorPeriod}) =>
               vendorPeriod.lower === period.lower && vendorPeriod.upper === period.upper
         );
      },
      assignedUsers: (state, getters, rootState, rootGetters) =>
         getVisibleAssignedUsers({
            importedVendors: getters.importedVendors,
            manualVendors: getters.manualVendors,
            isSME: rootGetters.isSME,
            profileId: rootGetters.profile?.id,
         }),
      manualVendorsByPeriod: (state, getters) => (periodId) => {
         if (sectionName !== 'employees') return getters.manualVendors;

         const idsWithRealRow = getters.employeeIdsWithRealRowForPeriod(periodId);
         return getters.manualVendors.filter(({id}) => idsWithRealRow.has(id));
      },
      assignedUsersByPeriod: (state, getters, rootState, rootGetters) => (periodId) =>
         getVisibleAssignedUsers({
            importedVendors: getters.importedVendorsByPeriod(periodId),
            manualVendors: getters.manualVendorsByPeriod(periodId),
            isSME: rootGetters.isSME,
            profileId: rootGetters.profile?.id,
         }),
      accountCountsByAssigneeByPeriod: (state, getters) => (periodId) => {
         const allVendors = [
            ...getters.importedVendorsByPeriod(periodId),
            ...getters.manualVendorsByPeriod(periodId),
         ];

         return allVendors.reduce((counts, {assigneeId}) => {
            counts[assigneeId || null] ??= 0;
            counts[assigneeId || null]++;
            return counts;
         }, {});
      },
      // Deleting a project/activity leaves its id orphaned in older summary
      // rows' projectsData/activitiesData -- filter status computation down to
      // currently-valid ids so a stale leftover can't stop a row from ever
      // reading Complete again (see helpers/utils.js's dataStatus/
      // isProjectSplitComplete/isActivitySplitComplete).
      crossPeriodMergedStatuses: (state, getters, rootState, rootGetters) =>
         collectStatusesByAssignee({
            periodIds: getters.supportedPeriodIds,
            accountData: state.accountData,
            adHocData: filterEmployeePlaceholderRows(sectionName, state.adHocData),
            hasActivitiesDimension: sectionName === 'employees',
            ignoreEmployeeNetAmountRequirement:
               sectionName === 'employees' &&
               !(rootGetters.isRndig || rootGetters['users/currentUserPermissionFlags']?.payroll),
            resolveAssigneeId: (item) =>
               resolveCanonicalAssigneeId(item, {
                  vendorsMap: getters.vendorsMap,
                  importedVendorsMap: getters.importedVendorsMap,
               }),
            resolveValidProjectIds: (periodId) =>
               (rootGetters['projects/projectsInPeriod']?.(periodId) ?? []).map(({id}) =>
                  String(id)
               ),
            validActivityIds: (rootGetters['activities/activities'] ?? []).map(({id}) =>
               String(id)
            ),
            // Employees-only: once a value has been edited since flags were last
            // loaded (state.flagsStale), don't let a not-yet-re-validated flag keep
            // downgrading status -- it reverts to whatever the raw data completeness
            // says (Complete, if the data itself is complete), the same override
            // "Mark Assignment Complete" itself gets (see isAssignmentMarkCompletedDisabled).
            // Other sections' assignees don't edit the flagged value the same way, so
            // their downgrade stays sticky until flags are genuinely reloaded.
            hasUnresolvedFlagsForAssignee:
               sectionName === 'employees' && state.flagsStale
                  ? null
                  : buildHasUnresolvedFlagsForAssignee({getters, rootGetters}),
         }),
      mergedStatusesByPeriod: (state, getters, rootState, rootGetters) => (periodId) =>
         collectStatusesByAssignee({
            periodIds: [periodId],
            accountData: state.accountData,
            adHocData: filterEmployeePlaceholderRows(sectionName, state.adHocData),
            includeAssigneeIds: getters.assignedUsersByPeriod(periodId),
            hasActivitiesDimension: sectionName === 'employees',
            ignoreEmployeeNetAmountRequirement:
               sectionName === 'employees' &&
               !(rootGetters.isRndig || rootGetters['users/currentUserPermissionFlags']?.payroll),
            resolveAssigneeId: (item) =>
               resolveCanonicalAssigneeId(item, {
                  vendorsMap: getters.vendorsMap,
                  importedVendorsMap: getters.importedVendorsMap,
               }),
            resolveValidProjectIds: (pid) =>
               (rootGetters['projects/projectsInPeriod']?.(pid) ?? []).map(({id}) => String(id)),
            validActivityIds: (rootGetters['activities/activities'] ?? []).map(({id}) =>
               String(id)
            ),
            // Employees-only: once a value has been edited since flags were last
            // loaded (state.flagsStale), don't let a not-yet-re-validated flag keep
            // downgrading status -- it reverts to whatever the raw data completeness
            // says (Complete, if the data itself is complete), the same override
            // "Mark Assignment Complete" itself gets (see isAssignmentMarkCompletedDisabled).
            // Other sections' assignees don't edit the flagged value the same way, so
            // their downgrade stays sticky until flags are genuinely reloaded.
            hasUnresolvedFlagsForAssignee:
               sectionName === 'employees' && state.flagsStale
                  ? null
                  : buildHasUnresolvedFlagsForAssignee({getters, rootGetters}),
         }),

      ...getters,
   },

   mutations: {
      registerSaveRequest(state, {key, requestId}) {
         Vue.set(state.latestSaveRequestByKey, key, requestId);
      },

      clearSaveRequest(state, {key, requestId}) {
         if (state.latestSaveRequestByKey[key] === requestId) {
            Vue.delete(state.latestSaveRequestByKey, key);
         }
      },

      markFlagsStale(state) {
         state.flagsStale = true;
      },

      clearFlagsStale(state) {
         state.flagsStale = false;
      },

      clearVendors(state) {
         state.importedVendors = {};
         state.manualVendors = {};
      },

      clearData(state) {
         state.accountData = {};
         state.adHocData = {};
      },

      setImportedVendors({importedVendors}, importedVendor) {
         Vue.set(importedVendors, importedVendor.id, importedVendor);
      },

      setManualVendors({manualVendors}, manualVendor) {
         Vue.set(manualVendors, manualVendor.id, manualVendor);
      },

      setAccountData({accountData}, importedVendorSummaryByPeriod) {
         Object.keys(importedVendorSummaryByPeriod).forEach((periodId) => {
            Vue.set(accountData, periodId, importedVendorSummaryByPeriod[periodId]);
         });
      },

      setAdHocData({adHocData}, manualVendorSummaryByPeriod) {
         Object.keys(manualVendorSummaryByPeriod).forEach((periodId) => {
            Vue.set(adHocData, periodId, manualVendorSummaryByPeriod[periodId]);
         });
      },

      mergeSummaryData(state, {accountData: nextAccountData = {}, adHocData: nextAdHocData = {}}) {
         Object.keys(nextAccountData).forEach((periodId) => {
            if (!(periodId in state.accountData)) {
               Vue.set(state.accountData, periodId, {});
            }

            const periodRows = state.accountData[periodId];
            const nextRowsByAccount = nextAccountData[periodId] || {};
            Object.keys(nextRowsByAccount).forEach((account) => {
               const incomingRows = nextRowsByAccount[account] || [];
               if (!(account in periodRows)) {
                  Vue.set(periodRows, account, [...incomingRows]);
                  return;
               }

               const existingRows = periodRows[account];
               incomingRows.forEach((incomingRow) => {
                  const existingIndex = existingRows.findIndex((row) => {
                     if (incomingRow.id !== null && incomingRow.id !== undefined) {
                        return row.id === incomingRow.id;
                     }
                     return (
                        row.vendor === incomingRow.vendor &&
                        locationsMatch(row.location, incomingRow.location)
                     );
                  });

                  if (existingIndex >= 0) {
                     Vue.set(existingRows, existingIndex, incomingRow);
                  } else {
                     existingRows.push(incomingRow);
                  }
               });
            });
         });

         Object.keys(nextAdHocData).forEach((periodId) => {
            const incomingRows = nextAdHocData[periodId] || [];
            if (!(periodId in state.adHocData)) {
               Vue.set(state.adHocData, periodId, [...incomingRows]);
               return;
            }

            const existingRows = state.adHocData[periodId];
            incomingRows.forEach((incomingRow) => {
               const incomingManualId = getManualVendorId(incomingRow);
               const existingIndex = existingRows.findIndex((row) => {
                  if (incomingRow.id !== null && incomingRow.id !== undefined) {
                     if (row.id === incomingRow.id) return true;
                     // Replace a dummy (id: null) that matches by vendor identity —
                     // this happens when the server assigns an id to a row that was
                     // previously only a client-side placeholder.
                     if (
                        row.id === null &&
                        incomingManualId !== null &&
                        incomingManualId !== undefined &&
                        String(getManualVendorId(row)) === String(incomingManualId) &&
                        locationsMatch(row.location, incomingRow.location)
                     )
                        return true;
                     return false;
                  }

                  if (incomingManualId !== null && incomingManualId !== undefined) {
                     return (
                        String(getManualVendorId(row)) === String(incomingManualId) &&
                        locationsMatch(row.location, incomingRow.location)
                     );
                  }

                  return false;
               });

               if (existingIndex >= 0) {
                  const existingRow = existingRows[existingIndex];
                  // The create-summary endpoint's response has no assignee concept --
                  // expense_summary has no assignee_id column; it's only ever joined in
                  // from BaseAssignment by the GET/list query. Preserve the existing
                  // row's assigneeId rather than letting a fresh create silently null it
                  // out, which would drop the row out of every accordion until reload.
                  Vue.set(existingRows, existingIndex, {
                     ...incomingRow,
                     assigneeId: incomingRow.assigneeId ?? existingRow.assigneeId,
                  });
               } else {
                  existingRows.push(incomingRow);
               }
            });
         });
      },

      initializeAdHocData(
         {adHocData},
         {manualVendors, activeStudyPeriods, projectsSupportedPeriodIds, projectsData, sectionName}
      ) {
         manualVendors.forEach((vendor) => {
            activeStudyPeriods.forEach((studyPeriod) => {
               const periodId = studyPeriod.id;

               // TODO: Remove when unconfigured project assignment is supported
               // Skip if there is no assigned project.
               if (!projectsSupportedPeriodIds.includes(periodId)) return;

               if (!(periodId in adHocData)) {
                  Vue.set(adHocData, periodId, []);
               } else {
                  const hasData = adHocData[periodId].some(
                     (entry) =>
                        getManualVendorId(entry) === vendor.id &&
                        locationsMatch(entry.location, vendor.location)
                  );
                  if (hasData) {
                     return;
                  }
               }

               adHocData[periodId].push(
                  buildDummySummary({sectionName, period: studyPeriod, vendor, projectsData})
               );
            });
         });
      },

      deleteImportedVendor({importedVendors}, {vendorId}) {
         Vue.delete(importedVendors, vendorId);
      },

      deleteManualVendor({manualVendors}, {vendorId}) {
         Vue.delete(manualVendors, vendorId);
      },

      updateImportedVendorProjectPercentage(
         {accountData},
         {account, vendor, projectId, periodId, location, value}
      ) {
         const {rows, index} = findImportedVendorRowIndex({
            accountData,
            periodId,
            account,
            vendor,
            location,
         });
         if (index < 0) return;

         setProjectPercentageValue({row: rows[index], projectId, value});
      },

      updateManualVendor({manualVendors, importedVendors}, {assigneeId, vendorId}) {
         // Employees' imported entities are keyed by the same real entity id as manual
         // ones (see normalizedImportedVendors in AssignVendorModal.vue), so `vendorId`
         // here may live in either dict -- unlike contractors/supplies/cloud, where an
         // imported row's id-space never collides with a manual vendorId.
         if (manualVendors[vendorId]) {
            Vue.set(manualVendors[vendorId], 'assigneeId', assigneeId);
         } else if (importedVendors[vendorId]) {
            Vue.set(importedVendors[vendorId], 'assigneeId', assigneeId);
         }
      },

      updateImportedVendor({importedVendors}, {assigneeId, id}) {
         Vue.set(importedVendors[id], 'assigneeId', assigneeId);
      },

      updateManualVendorProjectPercentage(
         {adHocData},
         {vendorId, contractorId, location, projectId, periodId, value}
      ) {
         const {rows, index} = findManualVendorRowIndex({
            adHocData,
            periodId,
            vendorId,
            contractorId,
            location,
         });
         if (index < 0) return;

         setProjectPercentageValue({row: rows[index], projectId, value});
      },

      updateImportedVendorPercentage({accountData}, {account, vendor, periodId, location, value}) {
         const {rows, index} = findImportedVendorRowIndex({
            accountData,
            periodId,
            account,
            vendor,
            location,
         });
         if (index < 0) return;

         const row = rows[index];
         if (value === 0) {
            zeroProjectsFromAllKnownKeys(row);
         }
         setPercentageWithTimestamp({row, value});
      },

      updateManualVendorPercentage(
         {adHocData},
         {vendorId, contractorId, location, periodId, value}
      ) {
         const {rows, index} = findManualVendorRowIndex({
            adHocData,
            periodId,
            vendorId,
            contractorId,
            location,
         });
         if (index < 0) return;

         const row = rows[index];
         if (value === 0) {
            zeroProjectsFromAllKnownKeys(row);
         }
         setPercentageWithTimestamp({row, value});
      },

      updateManualVendorNetAmount(
         {adHocData},
         {vendorId, contractorId, location, periodId, value}
      ) {
         const {rows, index} = findManualVendorRowIndex({
            adHocData,
            periodId,
            vendorId,
            contractorId,
            location,
         });
         if (index < 0) return;

         const row = rows[index];
         if (value === 0) {
            setPercentageWithTimestamp({row, value});
            zeroProjectsFromAllKnownKeys(row);
         }
         applyNetAmountToRowBySection({sectionName, Vue, row, value});
      },

      applySavedPercentage(
         state,
         {summary, percentage, projectIds, activityIds, singleProjectId, singleActivityId}
      ) {
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         setPercentageWithTimestamp({row, value: percentage});

         if (percentage === 0) {
            zeroProjectsFromAllKnownKeys(row, projectIds);
            zeroActivitiesFromAllKnownKeys(row, activityIds);
         }
         if (singleProjectId != null) {
            setSingleProjectValue({row, projectId: singleProjectId, value: 100});
         }
         if (singleActivityId != null) {
            setSingleActivityValue({row, activityId: singleActivityId, value: 100});
         }
      },

      applySavedVendorProjectsData(state, {summary, projectsData}) {
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         setProjectsDataWithColumns({row, projectsData});
      },

      applySavedProjectPercentage(state, {summary, projectId, value}) {
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         setSingleProjectValue({row, projectId, value});
      },

      applySavedActivityPercentage(state, {summary, activityId, value}) {
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         setSingleActivityValue({row, activityId, value});
      },

      applySavedNetAmount(state, {summary, netAmount}) {
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         Vue.set(row, 'totalCredit', 0);
         applyNetAmountToRowBySection({sectionName, Vue, row, value: netAmount});

         if (row.netAmount?.saveSuccessful) {
            row.netAmount.saveSuccessful();
         }
      },

      rollbackSavedPercentage(state, {summary, snapshot}) {
         if (!snapshot) return;
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         setPercentageWithTimestamp({row, value: snapshot.percentage});
         setProjectsDataWithColumns({row, projectsData: cloneProjectsData(snapshot.projectsData)});
         setActivitiesDataWithColumns({
            row,
            activitiesData: cloneActivitiesData(snapshot.activitiesData),
         });
      },

      rollbackSavedProjectPercentage(state, {summary, projectId, previousValue}) {
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         setSingleProjectValue({row, projectId, value: previousValue});
      },

      rollbackSavedActivityPercentage(state, {summary, activityId, previousValue}) {
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         setSingleActivityValue({row, activityId, value: previousValue});
      },

      rollbackSavedNetAmount(state, {summary, snapshot}) {
         if (!snapshot) return;
         const row = findSummaryRowInState(state, summary);
         if (!row) return;

         Vue.set(row, 'totalCredit', snapshot.totalCredit ?? 0);
         rollbackNetAmountToRowBySection({sectionName, Vue, row, snapshot});

         setPercentageWithTimestamp({row, value: snapshot.percentage});
         setProjectsDataWithColumns({row, projectsData: cloneProjectsData(snapshot.projectsData)});
         setActivitiesDataWithColumns({
            row,
            activitiesData: cloneActivitiesData(snapshot.activitiesData),
         });
      },

      updateAdHocDataEntryAssigneeId({adHocData}, {assigneeId, vendorId}) {
         Object.values(adHocData).forEach((periodAssignments) => {
            periodAssignments.forEach((entry) => {
               // Placeholder rows (id === null) are seeded fresh on every load and never
               // persisted -- skip them so this doesn't touch every period at once.
               if (entry.id !== null && getManualVendorId(entry) === vendorId) {
                  Vue.set(entry, 'assigneeId', assigneeId);
               }
            });
         });
      },

      updateAccountDataEntryAssigneeId({accountData}, {assigneeId, vendor, account, periodId}) {
         accountData[periodId][account].forEach((entry) => {
            if (entry.vendor === vendor) Vue.set(entry, 'assigneeId', assigneeId);
         });
      },

      ...mutations,
   },

   actions: {
      /**
       * Refreshes verification flags for the current expense section.
       * No-op when:
       * - companyId is missing
       * - the current section is contractors (flags not supported)
       * @param {{dispatch: Function, getters: Object, rootGetters: Object}} store
       * @param {{companyId: number|string|null|undefined}} params
       * @returns {Promise<void>}
       */
      async _refreshSectionFlags({dispatch, getters, rootGetters}, {companyId}) {
         if (!companyId || !getters.flagCheckSection || sectionName === 'contractors') {
            return;
         }

         // No period filter -- flags/loadFlags replaces the whole store slice on every
         // call (see flags.js's loadFlags action), and ExpensesCard.vue/ExpensesIntro.vue's
         // hasUnresolvedFlags (feeding "Mark Section Complete") need to see unresolved
         // flags across every period, not just whichever one triggered this refresh.
         await dispatch(
            'flags/loadFlags',
            {
               companyId,
               checkSection: getters.flagCheckSection,
               studyId: rootGetters['companies/activeStudyId'],
            },
            {root: true}
         );
      },

      /**
       * Saves section-level declaration/completion data on the active study.
       * @param {{commit: Function}} store
       * @param {{companyId: number|string, periodId?: {lower: string|number, upper: string|number}, payload: Object}} params
       * @returns {Promise<void>}
       */
      async _saveSectionData({commit}, {companyId, periodId, payload}) {
         let response;
         if (periodId) {
            response = await this._vm.$http.put(
               `/api/company/${companyId}/${sectionName}?period=[${periodId.lower},${periodId.upper}]`,
               payload
            );
         } else {
            response = await this._vm.$http.put(
               `/api/company/${companyId}/${sectionName}`,
               payload
            );
         }

         commit(
            'companies/setExpenses',
            {
               section: sectionName,
               data: response.data,
            },
            {root: true}
         );
      },

      /**
       * Creates missing summary rows for periods and merges the response into local state.
       * @param {{commit: Function, rootGetters: Object}} store
       * @param {{companyId: number|string, periods: Array<Object>}} params
       * @returns {Promise<Array<Object>>}
       */
      async _createAndMergeSummaryPeriods({commit, rootGetters}, {companyId, periods}) {
         const {data: summaryData} = await this._vm.$http.post(`/api/${sectionName}/summary`, {
            companyId,
            periods,
         });

         const summaries = extractSummaryResults(summaryData);
         if (summaries.length === 0) {
            return summaries;
         }

         const {accountData, adHocData} = mapSummariesToVendorEntries(
            summaries,
            rootGetters['companies/studyPeriods'],
            rootGetters['projects/projectsSupportedPeriodIds']
         );

         commit('mergeSummaryData', {accountData, adHocData});
         return summaries;
      },

      /**
       * Returns the summary id, creating the summary row first if it does not exist yet.
       * Shows a user-facing alert and throws when the row cannot be created.
       * @param {{dispatch: Function, commit: Function}} store
       * @param {{summary: Object, companyId: number|string, fallbackMsg: string}} params
       * @returns {Promise<number|string>}
       */
      async _ensureSummaryId({dispatch, commit}, {summary, companyId, fallbackMsg}) {
         if (!isNil(summary?.id)) {
            return summary.id;
         }

         const period = summary.period;
         const periods = [
            {
               companyId,
               period: period.period,
               location: summary.location,
               assigneeId: summary.assigneeId ?? null,
               percentage: null,
               projectsData: {},
               totalCredit: 0,
               ...(summary.vendorId != null ? {vendorId: summary.vendorId} : {}),
               ...(summary.contractorId != null ? {contractorId: summary.contractorId} : {}),
            },
         ];

         const created = await dispatch('_createAndMergeSummaryPeriods', {companyId, periods});

         const match = created.find(
            (s) =>
               (summary.vendorId != null
                  ? s.vendorId === summary.vendorId
                  : s.contractorId === summary.contractorId) &&
               s.period?.upper === period.period?.upper
         );

         if (!match?.id) {
            commit('showAlert', {fallbackMsg}, {root: true});
            throw new Error('Failed to create summary row before save.');
         }

         return match.id;
      },

      /**
       * Emits a standardized save-failure alert message.
       * @param {{commit: Function}} store
       * @param {{error: Object, fallbackMsg: string}} params
       * @returns {void}
       */
      _showSaveAlert({commit}, {error, fallbackMsg}) {
         commit(
            'showAlert',
            {
               response: error?.response,
               fallbackMsg,
            },
            {root: true}
         );
      },

      /**
       * Resets assignment completion state for the summary's assignee+period.
       * @param {{commit: Function}} store
       * @param {{summary: Object}} params
       * @returns {void}
       */
      _resetActiveStudyAssignment({commit}, {summary}) {
         commit(
            'companies/resetActiveStudyAssignment',
            {
               assigneeId: summary.assigneeId,
               period: summary.period.period,
               sectionName,
            },
            {root: true}
         );
      },

      /**
       * Runs shared optimistic-save flow for summary field updates.
       * @param {{commit: Function, dispatch: Function, state: Object}} store
       * @param {{
       *   key: string,
       *   summary: Object,
       *   applyMutations: Array<{type: string, payload: Object}>,
       *   request: Function,
       *   rollbackMutation: string,
       *   rollbackPayload: Object,
       *   fallbackMsg: string
       * }} params
       * @returns {Promise<void>}
       */
      async _runOptimisticSummarySave(
         {commit, dispatch, state},
         {key, summary, applyMutations, request, rollbackMutation, rollbackPayload, fallbackMsg}
      ) {
         const requestId = ++_saveRequestCounter;

         commit('registerSaveRequest', {key, requestId});
         applyMutations.forEach(({type, payload}) => {
            commit(type, payload);
         });

         try {
            await request();
            if (state.latestSaveRequestByKey[key] !== requestId) return;

            commit('clearSaveRequest', {key, requestId});
            commit('markFlagsStale');
            dispatch('_resetActiveStudyAssignment', {summary});
         } catch (error) {
            if (state.latestSaveRequestByKey[key] === requestId) {
               commit(rollbackMutation, rollbackPayload);
               commit('clearSaveRequest', {key, requestId});
               dispatch('_showSaveAlert', {error, fallbackMsg});
            }
            throw error;
         }
      },

      /** Update the section declaration */
      async saveDeclaration({dispatch}, {companyId, declaration}) {
         const payload = {wasUsed: declaration};
         await dispatch('_saveSectionData', {companyId, payload});
      },

      /** Submit the section */
      async submit({dispatch}, {companyId, periodId}) {
         const payload = {completed: true};
         await dispatch('_saveSectionData', {companyId, periodId, payload});
      },

      /** Unlock the section */
      async unlock({dispatch}, {companyId}) {
         const payload = {completed: false};
         await dispatch('_saveSectionData', {companyId, payload});
      },

      async createAssignments({commit, rootGetters}, {assignments, profileId}) {
         const {data} = await this._vm.$http.post(
            `/api/user/${profileId}/${sectionName}/assignment`,
            {
               assignments,
            }
         );

         const createdAssignments = data.results || [];
         const activeStudy = rootGetters['companies/activeStudy'];
         const currentAssignments = activeStudy?.[resolveAssignmentType(sectionName)] || [];
         if (createdAssignments.length > 0) {
            commit(
               'companies/setExpensesAssignments',
               {
                  section: sectionName,
                  assignments: [...currentAssignments, ...createdAssignments],
               },
               {root: true}
            );
         }

         assignments.forEach(
            ({vendorId, contractorId, employeeId, vendor, account, assigneeId, id, speriodId}) => {
               const manualId = contractorId ?? vendorId ?? employeeId;
               if (manualId) {
                  commit('updateAdHocDataEntryAssigneeId', {assigneeId, vendorId: manualId});
                  commit('updateManualVendor', {assigneeId, vendorId: manualId});
               } else {
                  commit('updateAccountDataEntryAssigneeId', {
                     assigneeId,
                     vendor,
                     account,
                     periodId: speriodId,
                  });
                  commit('updateImportedVendor', {assigneeId, id});
               }
            }
         );
      },

      async removeAssignments({commit, dispatch, rootGetters}, {assignments, profileId}) {
         await this._vm.$http.delete(`/api/user/${profileId}/${sectionName}/assignment`, {
            data: {assignments},
         });

         assignments.forEach(({assigneeId, period}) => {
            commit(
               'companies/resetActiveStudyAssignment',
               {
                  assigneeId,
                  period,
                  sectionName,
               },
               {root: true}
            );
         });

         assignments.forEach(
            ({vendorId, contractorId, employeeId, vendor, account, id, speriodId}) => {
               const assigneeId = null;
               const manualId = contractorId ?? vendorId ?? employeeId;
               if (manualId) {
                  commit('updateAdHocDataEntryAssigneeId', {assigneeId, vendorId: manualId});
                  commit('updateManualVendor', {assigneeId, vendorId: manualId});
               } else {
                  commit('updateAccountDataEntryAssigneeId', {
                     assigneeId,
                     vendor,
                     account,
                     periodId: speriodId,
                  });
                  commit('updateImportedVendor', {assigneeId, id});
               }
            }
         );

         const companyId = assignments?.[0]?.companyId || rootGetters.profile?.companyId;
         await dispatch('_refreshSectionFlags', {companyId});
      },

      async loadAllAssignmentsByCompanyId({commit, rootGetters}, {companyId}) {
         const {
            data: {importedVendors, manualVendors},
         } = await this._vm.$http.get(
            `/api/${sectionName}/assignment?all=true&company_id=${companyId}`
         );

         const activeStudyId = rootGetters['companies/activeStudyId'];
         const projectsSupportedPeriodIds = rootGetters['projects/projectsSupportedPeriodIds'];
         const studyPeriodMap = rootGetters['companies/studyPeriodMap'];

         // TODO: Remove when unconfigured project assignment is supported
         const projectsSupportedPeriods = [];
         projectsSupportedPeriodIds.forEach((id) =>
            projectsSupportedPeriods.push(studyPeriodMap[id].period)
         );

         const importedVendorsByStudyId = importedVendors.filter(
            ({studyId}) => Number(studyId) === Number(activeStudyId)
         );
         const manualVendorsByStudyId = manualVendors.filter(
            ({studyId}) => Number(studyId) === Number(activeStudyId)
         );

         commit('clearVendors');
         // TODO: Remove when unconfigured project assignment is supported
         // Skip if there is no assigned project.
         filterVendorsBySupportedPeriods(
            importedVendorsByStudyId,
            projectsSupportedPeriods
         ).forEach((vendor) => commit('setImportedVendors', vendor));
         manualVendorsByStudyId.forEach((vendor) =>
            commit('setManualVendors', normalizeManualVendor(sectionName, vendor))
         );
      },

      // TODO: should be renamed
      async loadData({commit, dispatch, rootGetters}, {companyId = null}) {
         const params = companyId ? {company_id: companyId} : {};
         const {data: summaryData} = await this._vm.$http.get(`/api/${sectionName}/summary`, {
            params,
         });

         const summaries = extractSummaryResults(summaryData);
         const {accountData, adHocData} = mapSummariesToVendorEntries(
            summaries,
            rootGetters['companies/studyPeriods'],
            rootGetters['projects/projectsSupportedPeriodIds']
         );

         commit('clearData');
         commit('setAccountData', accountData);
         commit('setAdHocData', adHocData);
         dispatch('initializeAdHocDataAction');
      },

      async initializeAdHocDataAction({commit, getters, rootGetters}) {
         const activeStudyPeriods = rootGetters['companies/activeStudyPeriods'];
         const manualVendors = getters.manualVendors;
         const projectsSupportedPeriodIds = rootGetters['projects/projectsSupportedPeriodIds'];
         const projects = rootGetters['projects/projectsInStudy'];
         const projectsData = {};
         projects.forEach(({id}) => (projectsData[id] = null));

         commit('initializeAdHocData', {
            manualVendors,
            activeStudyPeriods,
            projectsSupportedPeriodIds,
            projectsData,
            sectionName,
         });
      },

      async setZero({commit, dispatch, state, rootGetters}, {companyId, summaries}) {
         const resolvedCompanyId =
            companyId ??
            rootGetters['companies/currentCompany']?.id ??
            rootGetters.profile?.companyId;

         const snapshots = summaries.map((summary) => ({
            summary,
            snapshot: snapshotSummaryRow(state, summary),
         }));

         snapshots.forEach(({summary}) => {
            const {projectIds, activityIds} = resolveZeroableSplitIds({
               sectionName,
               rootGetters,
               periodId: summary?.period?.id,
            });
            commit('applySavedPercentage', {summary, percentage: 0, projectIds, activityIds});
         });

         const byAssignee = summaries.reduce((groups, summary) => {
            const key = summary.assigneeId ?? '__UNASSIGNED__';
            if (!groups[key]) groups[key] = [];
            groups[key].push(summary);
            return groups;
         }, {});

         try {
            await Promise.all(
               Object.values(byAssignee).map((group) => {
                  const names = group
                     .filter((s) => s.account && s.vendor)
                     .map(({account, vendor, location}) => ({account, vendor, location}));
                  const ids = group
                     .filter((s) => getManualVendorId(s) != null)
                     .map((s) => getManualVendorId(s));
                  const {assigneeId, period} = group[0];
                  return this._vm.$http.post(`/api/${sectionName}/summary/set-zero`, {
                     companyId: resolvedCompanyId,
                     period: period?.period,
                     names,
                     ids,
                     assigneeId,
                  });
               })
            );

            Object.values(byAssignee).forEach((group) => {
               const {assigneeId, period} = group[0];
               commit(
                  'companies/resetActiveStudyAssignment',
                  {assigneeId, period: period?.period, sectionName},
                  {root: true}
               );
            });
         } catch (error) {
            snapshots.forEach(({summary, snapshot}) => {
               commit('rollbackSavedPercentage', {summary, snapshot});
            });
            dispatch('_showSaveAlert', {
               error,
               fallbackMsg: 'Unable to set to zero. Please retry.',
            });
            throw error;
         }
      },

      async createManualVendor(
         {commit, dispatch, getters, rootGetters},
         {name, location = null, companyId, periodIds = null, ...extraPayload}
      ) {
         const payload = manualVendorPayload({
            sectionName,
            payload: {name, location, companyId, ...extraPayload},
            getters,
            rootGetters,
         });
         const {data} = await this._vm.$http.post(`/api/${sectionName}/vendor`, payload);
         const manualVendor = normalizeManualVendor(sectionName, data);
         commit('setManualVendors', manualVendor);
         const activeStudyPeriods = rootGetters['companies/activeStudyPeriods'] || [];
         const projectsSupportedPeriodIds =
            rootGetters['projects/projectsSupportedPeriodIds'] || [];
         const periods = buildManualSummaryPeriodsPayload({
            sectionName,
            companyId,
            manualVendor,
            activeStudyPeriods,
            projectsSupportedPeriodIds,
            periodIds,
         });

         if (periods.length > 0) {
            await dispatch('_createAndMergeSummaryPeriods', {companyId, periods});
         }
         return manualVendor;
      },

      async updateManualVendor(
         {commit, getters, rootGetters},
         {vendorId, companyId, name, location = null, ...extraPayload}
      ) {
         const payload = manualVendorPayload({
            sectionName,
            payload: {
               vendorId,
               companyId,
               name,
               location,
               ...extraPayload,
            },
            getters,
            rootGetters,
         });
         const {data} = await this._vm.$http.put(`/api/${sectionName}/vendor/${vendorId}`, payload);
         const manualVendor = normalizeManualVendor(sectionName, data);
         commit('setManualVendors', manualVendor);
         return manualVendor;
      },

      async deleteManualVendor(
         {commit, state, dispatch, rootGetters},
         {vendorId, force = false, companyId = null}
      ) {
         await this._vm.$http.delete(`/api/${sectionName}/vendor/${vendorId}`, {
            params: {force},
         });
         commit('deleteManualVendor', {vendorId});

         const pruneVendorFromSummary = (data, vendorIdToRemove) => {
            return Object.fromEntries(
               Object.entries(data).map(([periodId, periodData]) => [
                  periodId,
                  periodData.filter((entry) => getManualVendorId(entry) !== vendorIdToRemove),
               ])
            );
         };

         commit('setAdHocData', pruneVendorFromSummary(state.adHocData, vendorId));

         const resolvedCompanyId =
            companyId ??
            rootGetters['companies/currentCompany']?.id ??
            rootGetters.profile?.companyId;
         await dispatch('_refreshSectionFlags', {companyId: resolvedCompanyId});
      },

      async savePercentage({dispatch, state, rootGetters}, {companyId, summary, percentage}) {
         const summaryId = await dispatch('_ensureSummaryId', {
            summary,
            companyId,
            fallbackMsg: 'Unable to save percentage right now. Please refresh and retry.',
         });
         const key = buildSaveRequestKey({summaryId, field: 'percentage'});
         const snapshot = snapshotSummaryRow(state, summary);
         const setZeroPayload = buildSetZeroPayload({
            summary,
            companyId,
            fallbackCompanyId: rootGetters.profile?.companyId,
         });
         const {projectIds, activityIds} = resolveZeroableSplitIds({
            sectionName,
            rootGetters,
            periodId: summary?.period?.id,
         });
         // With exactly one project/activity there's nowhere else the split could
         // go -- auto-fill it to 100% instead of making the user type the one
         // obvious value. With more than one option the allocation is ambiguous,
         // so this only applies when there's exactly one (mirrors the percentage
         // === 0 case above, which unconditionally zeros every known project --
         // that's unambiguous regardless of count).
         const singleProjectId = percentage > 0 && projectIds.length === 1 ? projectIds[0] : null;
         const singleActivityId =
            percentage > 0 && activityIds.length === 1 ? activityIds[0] : null;

         await dispatch('_runOptimisticSummarySave', {
            key,
            summary,
            applyMutations: [
               {
                  type: 'applySavedPercentage',
                  payload: {
                     summary,
                     percentage,
                     projectIds,
                     activityIds,
                     singleProjectId,
                     singleActivityId,
                  },
               },
            ],
            request: async () => {
               if (percentage === 0 && setZeroPayload) {
                  return this._vm.$http.post(
                     `/api/${sectionName}/summary/set-zero`,
                     setZeroPayload
                  );
               }

               const response = await this._vm.$http.put(`/api/${sectionName}/summary/percentage`, {
                  summaryId,
                  assigneeId: summary.assigneeId,
                  percentage,
               });

               // Dispatch the real save actions (not a raw $http.put) so this
               // auto-fill registers under the exact same save-request keys
               // ('project'/projectId, 'activity'/activityId) that
               // saveProjectPercentage/saveActivityPercentage use. Without that,
               // an already in-flight independent save for the same field (e.g.
               // the user had started typing into it before entering the
               // percentage) has no way to be recognized as stale, and whichever
               // of the two PUTs the server happens to process last silently
               // wins -- which can leave the field empty in the DB even though
               // the UI optimistically shows 100%, only surfacing on refresh.
               // Stamp the now-resolved summaryId onto the summary passed down --
               // `summary` may still be the original dummy-row reference (id:
               // null) from before _ensureSummaryId ran above; without this,
               // saveProjectPercentage/saveActivityPercentage's own
               // _ensureSummaryId would see a null id and create a second,
               // duplicate summary row instead of reusing this one.
               const resolvedSummary = {...summary, id: summaryId};

               if (singleProjectId != null) {
                  await dispatch('saveProjectPercentage', {
                     summary: resolvedSummary,
                     projectId: singleProjectId,
                     value: 100,
                  });
               }
               if (singleActivityId != null) {
                  await dispatch('saveActivityPercentage', {
                     summary: resolvedSummary,
                     activityId: singleActivityId,
                     value: 100,
                  });
               }

               return response;
            },
            rollbackMutation: 'rollbackSavedPercentage',
            rollbackPayload: {summary, snapshot},
            fallbackMsg: 'Unable to save percentage. Please retry.',
         });
      },

      async saveProjectPercentage({dispatch, rootGetters, state}, {summary, projectId, value}) {
         const summaryId = await dispatch('_ensureSummaryId', {
            summary,
            companyId:
               rootGetters['companies/currentCompany']?.id ?? rootGetters.profile?.companyId,
            fallbackMsg: 'Unable to save project percentage right now. Please refresh and retry.',
         });
         const normalizedProjectId = String(projectId);
         const key = buildSaveRequestKey({
            summaryId,
            field: 'project',
            projectId: normalizedProjectId,
         });
         const snapshot = snapshotSummaryRow(state, summary);
         const previousValue = snapshot?.projectsData?.[normalizedProjectId] ?? null;

         await dispatch('_runOptimisticSummarySave', {
            key,
            summary,
            applyMutations: [
               {
                  type: 'applySavedProjectPercentage',
                  payload: {
                     summary,
                     projectId: normalizedProjectId,
                     value,
                  },
               },
            ],
            request: () =>
               this._vm.$http.put(`/api/${sectionName}/summary/project`, {
                  summaryId,
                  assigneeId: summary.assigneeId,
                  projectsData: {
                     [normalizedProjectId]: value,
                  },
               }),
            rollbackMutation: 'rollbackSavedProjectPercentage',
            rollbackPayload: {
               summary,
               projectId: normalizedProjectId,
               previousValue,
            },
            fallbackMsg: 'Unable to save project percentage. Please retry.',
         });
      },

      async saveActivityPercentage({dispatch, rootGetters, state}, {summary, activityId, value}) {
         const summaryId = await dispatch('_ensureSummaryId', {
            summary,
            companyId:
               rootGetters['companies/currentCompany']?.id ?? rootGetters.profile?.companyId,
            fallbackMsg: 'Unable to save activity percentage right now. Please refresh and retry.',
         });
         const normalizedActivityId = String(activityId);
         const key = buildSaveRequestKey({
            summaryId,
            field: 'activity',
            projectId: normalizedActivityId,
         });
         const snapshot = snapshotSummaryRow(state, summary);
         const previousValue = snapshot?.activitiesData?.[normalizedActivityId] ?? null;

         await dispatch('_runOptimisticSummarySave', {
            key,
            summary,
            applyMutations: [
               {
                  type: 'applySavedActivityPercentage',
                  payload: {
                     summary,
                     activityId: normalizedActivityId,
                     value,
                  },
               },
            ],
            request: () =>
               this._vm.$http.put(`/api/${sectionName}/summary/activity`, {
                  summaryId,
                  assigneeId: summary.assigneeId,
                  activitiesData: {
                     [normalizedActivityId]: value,
                  },
               }),
            rollbackMutation: 'rollbackSavedActivityPercentage',
            rollbackPayload: {
               summary,
               activityId: normalizedActivityId,
               previousValue,
            },
            fallbackMsg: 'Unable to save activity percentage. Please retry.',
         });
      },

      async saveNetAmount({dispatch, state, rootGetters}, {companyId, summary, netAmount}) {
         const summaryId = await dispatch('_ensureSummaryId', {
            summary,
            companyId,
            fallbackMsg: 'Unable to save payment right now. Please refresh and retry.',
         });
         const key = buildSaveRequestKey({summaryId, field: 'payment'});
         const snapshot = snapshotSummaryRow(state, summary);
         const setZeroPayload = buildSetZeroPayload({
            summary,
            companyId,
            fallbackCompanyId: rootGetters.profile?.companyId,
         });
         const applyMutations = [{type: 'applySavedNetAmount', payload: {summary, netAmount}}];
         if (netAmount === 0) {
            const {projectIds, activityIds} = resolveZeroableSplitIds({
               sectionName,
               rootGetters,
               periodId: summary?.period?.id,
            });
            applyMutations.push({
               type: 'applySavedPercentage',
               payload: {summary, percentage: 0, projectIds, activityIds},
            });
         }

         await dispatch('_runOptimisticSummarySave', {
            key,
            summary,
            applyMutations,
            request: () => {
               const payload = {summaryId, assigneeId: summary.assigneeId, payment: netAmount};

               return this._vm.$http
                  .put(`/api/${sectionName}/summary/payment`, payload)
                  .then(() => {
                     if (netAmount === 0 && setZeroPayload) {
                        return this._vm.$http.post(
                           `/api/${sectionName}/summary/set-zero`,
                           setZeroPayload
                        );
                     }
                  });
            },
            rollbackMutation: 'rollbackSavedNetAmount',
            rollbackPayload: {summary, snapshot},
            fallbackMsg: 'Unable to save payment amount. Please retry.',
         });
      },

      async uploadSupportingDocuments({dispatch}, {files, uploadCategoryId}) {
         if (files.length === 0 || !uploadCategoryId) {
            return;
         }

         const validation = {
            type: documentsUploadCategory,
         };

         await dispatch('uploads/uploadFiles', {uploadCategoryId, files, validation}, {root: true});
      },

      ...actions,
   },
});
